import { SdkUserAgentInfo } from './parseSdkUserAgent.types';

/** The SDK agent is exactly three `|`-separated segments — app, SDK, OS. */
const SEGMENT_COUNT = 3;

// Positional groups rather than named ones: `tsconfig.base.json` targets
// es2015, where `(?<name>…)` is a hard build error (TS1503) — and one the unit
// tests cannot see, since jest transpiles with babel against a newer target.
// The index constants are what keep that readable.

/** `<name> (<scheme>) <version>` — name and scheme are free text. */
const APP_SEGMENT = /^(.*?)\s*\(([^)]*)\)\s*(\S+)?$/;
const APP_NAME = 1;
const APP_SCHEME = 2;
const APP_VERSION = 3;

/** `BCSDK <version>` — the version token may or may not be dotted. */
const SDK_SEGMENT = /^BCSDK\s+(\S+)$/;
const SDK_VERSION = 1;

/** `<os> <version>` — the OS family is a single word. */
const OS_SEGMENT = /^([A-Za-z]+)(?:\s+(\S+))?$/;
const OS_NAME = 1;
const OS_VERSION = 2;

/** Split a `1.23#3131` token into its version and build halves. */
const splitBuild = (token: string | undefined) => {
  const [version, build] = (token || '').split('#');

  return { version: version || undefined, build: build || undefined };
};

/**
 * Drop keys whose value is `undefined`, so callers can spread the result and
 * `'device' in info` answers honestly.
 *
 * A loop rather than `Object.fromEntries`, which is ES2019 while
 * `tsconfig.base.json` declares `lib: ["es2017", "dom"]`.
 */
const compact = (info: SdkUserAgentInfo): SdkUserAgentInfo => {
  const out: Record<string, string> = {};

  Object.entries(info).forEach(([key, value]) => {
    if (value !== undefined) {
      out[key] = value;
    }
  });

  return out;
};

/**
 * Dissolve the SDK's user agent into its parts, or `null` for any user agent
 * that is not the SDK's.
 *
 * The SDK *replaces* the webview user agent wholesale with a fixed
 * three-segment string, built by `DeviceInfo.kt` on Android and
 * `DeviceInfo.swift` on iOS:
 *
 *     MobilePocket (appScheme) 1.23#3131 | BCSDK 1.00#121 | Android 14#build21, Samsung G3
 *     Bluecode (bluecode) 4711           | BCSDK 121       | iOS 17.4#21E219, iPhone15,2
 *
 * Both examples are real shapes, and they disagree on almost everything the
 * naive reader would assume: the version may be dotted or a bare build number,
 * the `#build` suffix is optional in every position, and the device model
 * itself contains a comma (`iPhone15,2`) — hence the split on the *first*
 * comma only.
 *
 * Returns `null` — never a partial guess — unless the string is three segments
 * whose middle one is the `BCSDK` marker. A plain browser, the sdk-simulator
 * and jsdom all carry an ordinary user agent, so callers must read `null` as
 * "not in an SDK webview", never as "an SDK webview that told us nothing".
 * {@link nativeService.getPlatform} takes the same line for the same reason.
 *
 * The marker is load-bearing rather than decorative, and the segment count
 * alone is not enough (Greptile review of #8857). `Totally Unrelated | Some
 * Thing | Whatever, Else` splits into three, so the OS regex reads `Whatever`
 * as an OS family and `Else` as a device model — and those become the
 * `os_version` and `device` super properties of an app that is not in an SDK
 * webview at all. Worse, the result is a truthy object, so a caller asking
 * `if (info)` concludes it *is* one. `BCSDK` is the one part of the format
 * nothing else produces by accident, which is why {@link isSdkAtLeast} has
 * always keyed on it, and requiring it also guarantees a non-null result
 * carries at least `sdkVersion` — so the object is never empty.
 */
export const parseSdkUserAgent = (
  userAgent: string | undefined | null,
): SdkUserAgentInfo | null => {
  const segments = userAgent?.split('|');

  if (segments?.length !== SEGMENT_COUNT) {
    return null;
  }

  const [appSegment, sdkSegment, osSegment] = segments.map((s) => s.trim());

  const sdk = SDK_SEGMENT.exec(sdkSegment);

  // No `BCSDK` marker, no SDK agent — see the note above on why three
  // segments on their own are not evidence of one.
  if (!sdk) {
    return null;
  }

  const app = APP_SEGMENT.exec(appSegment);

  // The device model may itself contain a comma, so only the first one
  // separates the OS from the model.
  const commaIndex = osSegment.indexOf(',');
  const osPart =
    commaIndex === -1 ? osSegment : osSegment.slice(0, commaIndex).trim();
  const device =
    commaIndex === -1 ? undefined : osSegment.slice(commaIndex + 1).trim();

  const os = OS_SEGMENT.exec(osPart);

  const hostApp = splitBuild(app?.[APP_VERSION]);
  const sdkParts = splitBuild(sdk?.[SDK_VERSION]);
  const osVersion = splitBuild(os?.[OS_VERSION]);

  return compact({
    hostAppName: app?.[APP_NAME] || undefined,
    hostAppScheme: app?.[APP_SCHEME] || undefined,
    hostAppVersion: hostApp.version,
    hostAppBuild: hostApp.build,
    sdkVersion: sdkParts.version,
    sdkBuild: sdkParts.build,
    osName: os?.[OS_NAME]?.toLowerCase(),
    osVersion: osVersion.version,
    osBuild: osVersion.build,
    device: device || undefined,
  });
};

export default parseSdkUserAgent;
